import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
  formatFractionLatexCore
} from "../../../../components/inputs/FractionInput";

function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  const maxDen = 10;
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;

      if (intPart === 0) return `${sign}\\dfrac{${num}}{${den}}`;
      return `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }

  return `${sign}${abs.toFixed(2)}`;
}

function getRandomInt(min, max) {
  let num;
  do {
    num = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (num === 0); // unika zera
  return num;
}

export function generateTask() {
  const A = getRandomInt(-5, 5);
  const B = getRandomInt(-5, 5);
  const C = getRandomInt(-5, 5);
  const a = getRandomInt(-5, 5);

  const m = (-1 / a - B) / A;

  return {
    type: "FindParameterPerpendicularTask",
    data: { A, B, C, a, b: getRandomInt(-5, 5) },
    answer: {
      raw: `${m}`,
      parsed: m
    }
  };
}

export function renderTask({ data }) {
  const { A, B, C, a, b } = data;

  const firstA = A === 1 ? "m" :
                 A === -1 ? "-m" :
                 `${A}m`;

  const firstExpr = B === 0 ? firstA :
    B > 0 ? `${firstA} + ${B}` :
    `${firstA} - ${Math.abs(B)}`;

  const cPart =
    C === 0 ? "" :
    C > 0 ? ` + ${C}` :
    ` - ${Math.abs(C)}`;

  // druga prosta: zależna od a i b
  let secondExpr;
  if (a === 0) {
    secondExpr = `${b}`; // y = b
  } else {
    const aExpr =
      a === 1 ? "x" :
      a === -1 ? "-x" :
      `${a}x`;

    secondExpr = b === 0 ? `${aExpr}` :
      b > 0 ? `${aExpr} + ${b}` :
      `${aExpr} - ${Math.abs(b)}`;
  }

  return (
    <div className="space-y-2">
      <p>
        Dana jest prosta <InlineMath math={`y = (${firstExpr})x${cPart}`} /> oraz prosta{" "}
        <InlineMath math={`y = ${secondExpr}`} />.
      </p>
      <p>
        Dla jakiej wartości parametru <InlineMath math="m" /> proste są prostopadłe?
      </p>
    </div>
  );
}



export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-4">
      <span><InlineMath math="m = " /></span>
      <FractionInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label="m"
      />
    </div>
  );
}

export function checkAnswer({ answer }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;

  const parsed = userAnswer?.parsed;
  if (parsed == null || isNaN(parsed)) return false;

  return Math.abs(parsed - answer.parsed) < 0.0001;
}

export function renderAnswer({ answer }) {
  const mLatex = formatMixedFractionLatex(answer.parsed);

  return (
    <div style={{ marginTop: "1rem" }}>
      <InlineMath math={`m = ${mLatex}`} />
    </div>
  );
}

export function renderExplanation({ data, answer }) {
  const { A, B, a } = data;

  const mLatex = formatMixedFractionLatex(answer.parsed);

  const aPrefix = A === 1 ? "" : A === -1 ? "-" : `${A}`;
  const left = `(${aPrefix}m ${B >= 0 ? "+ " + B : "- " + Math.abs(B)})`;
  const aFormatted = a < 0 ? `(${a})` : `${a}`;

  const rhsInitial = `-\\dfrac{1}{${a}}`;
  const rhsSimplified = a < 0 ? `\\dfrac{1}{${Math.abs(a)}}` : `-\\dfrac{1}{${a}}`;

  const subtraction = a < 0
    ? `\\dfrac{1}{${Math.abs(a)}} - ${Math.abs(B)}`
    : `-\\dfrac{1}{${a}} - ${Math.abs(B)}`;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Proste są prostopadłe, jeśli iloczyn ich współczynników kierunkowych wynosi <InlineMath math="-1" />:
        <BlockMath math={String.raw`a_{1}\cdot a_{2}=-1`} />
        <BlockMath math={`${left} \\cdot ${aFormatted} = -1`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Dzielimy obie strony przez <InlineMath math={`${aFormatted}`} /> i rozwiązujemy równanie:
        <BlockMath math={`${aPrefix}m ${B >= 0 ? "+ " + B : "- " + Math.abs(B)} = ${rhsSimplified}`} />
        <BlockMath math={`${aPrefix}m = ${subtraction}`} />
        <BlockMath math={`m = \\dfrac{${subtraction}}{${A}} = ${mLatex}`} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  const latex = formatLatexPreview(userAnswer.raw, "m");

  return (
    <div style={{ marginTop: "1rem" }}>
      <InlineMath math={latex} />
    </div>
  );
}
